import React, { useState, useEffect } from "react";
import { Crown, Clock, Shield, Star, Wrench, CheckCircle, Phone, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

const RistrutturazioniPrestigioSection = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const prestigeImages = [
    {
      url: "/images/about4.jpg",
      title: "Scala Artistica con Illuminazione LED",
      description: "Muratura in pietra antica con ringhiera in ferro battuto e sistema di illuminazione scenografico"
    },
    {
      url: "/images/about1.jpg",
      title: "Bagno di Lusso con Marmi Pregiati",
      description: "Rivestimenti in marmo Calacatta, sanitari sospesi di design e termoarredo elegante"
    },
    {
      url: "/images/about8.jpeg",
      title: "Camera da Letto Classica",
      description: "Decorazioni murali artistiche, colonne corinzie e dettagli architettonici storici"
    },
    {
      url: "/images/about2.jpg",
      title: "Dettagli Architettonici Storici",
      description: "Restauro conservativo con preservazione degli elementi originali e integrazione moderna"
    },
    {
      url: "/images/about5.jpeg",
      title: "Ristrutturazione Luxury Completa",
      description: "Ambiente prestigioso con finiture di alta gamma e cura maniacale dei dettagli"
    }
  ];

  // Auto-slide ogni 7 secondi per apprezzare i dettagli
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % prestigeImages.length);
    }, 7000);
    
    return () => clearInterval(interval);
  }, [prestigeImages.length]);

  const goToPrevious = () => {
    setCurrentImage((prev) => (prev - 1 + prestigeImages.length) % prestigeImages.length);
  };

  const goToNext = () => {
    setCurrentImage((prev) => (prev + 1) % prestigeImages.length);
  };

  const features = [
    "Analisi storico-architettonica preliminare",
    "Progettazione nel rispetto dei vincoli storici",
    "Materiali di pregio: marmi, pietre naturali, legni pregiati",
    "Restauro conservativo di elementi originali",
    "Integrazione discreta di tecnologie moderne",
    "Decorazioni artistiche e affreschi murali",
    "Sistemi di illuminazione scenografici LED",
    "Finiture luxury e dettagli su misura",
    "Collaborazione con maestranze specializzate",
    "Certificazioni storiche e vincoli artistici"
  ];

  const benefits = [
    "Valorizzazione patrimonio immobiliare storico",
    "Preservazione identità architettonica originale",
    "Aumento valore immobile del 40-60%",
    "Comfort moderno in ambienti storici",
    "Efficienza energetica nel rispetto dei vincoli",
    "Materiali certificati per edifici storici",
    "Progetti approvati dalle Soprintendenze",
    "Eccellenza artigianale Made in Italy"
  ];

  const specializations = [
    { name: "Palazzi Storici", description: "Restauro conservativo edifici d'epoca", icon: "🏛️" },
    { name: "Ville di Prestigio", description: "Ristrutturazioni luxury residenziali", icon: "🏰" },
    { name: "Locali Commerciali", description: "Spazi commerciali di alta gamma", icon: "🏪" },
    { name: "Edifici Vincolati", description: "Progetti con vincoli soprintendenza", icon: "⚖️" }
  ];

  return (
    <section id="prestigio-dettagli" className="py-20 lg:py-32 bg-gradient-to-br from-purple-50 to-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <Crown className="h-16 w-16 text-purple-600 mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Ristrutturazioni <span className="text-purple-600">di Prestigio</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed text-justify">
            <strong>Eccellenza nel restauro e nelle ristrutturazioni di lusso.</strong><br />
            Specializzati nel recupero di edifici storici e ristrutturazioni di prestigio, 
            combiniamo tradizione artigianale e innovazione per preservare il patrimonio 
            architettonico con comfort moderni.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Dynamic Image Slider */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl relative">
              {/* Images */}
              {prestigeImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-1200 ease-in-out ${
                    index === currentImage
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-105'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay elegante per prestigio */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-8 left-8 right-8 text-white">
                      <h4 className="text-xl font-bold mb-3">{image.title}</h4>
                      <p className="text-sm text-gray-200 leading-relaxed">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation Controls */}
              <button
                onClick={goToPrevious}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-purple-600/80 hover:bg-purple-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-purple-600/80 hover:bg-purple-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Elegant Dot Indicators */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
                {prestigeImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImage
                        ? 'bg-purple-400 scale-125 shadow-lg'
                        : 'bg-white/60 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>

              {/* Image Counter */}
              <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentImage + 1} / {prestigeImages.length}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Key Info Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="text-center">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">30-90</div>
                  <div className="text-sm text-gray-600">Giorni Lavorativi</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Shield className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">10 Anni</div>
                  <div className="text-sm text-gray-600">Garanzia</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">Luxury</div>
                  <div className="text-sm text-gray-600">Materiali</div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Description */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Maestri nelle Ristrutturazioni di Prestigio</h3>
              <p className="text-gray-700 leading-relaxed mb-6 text-justify">
                Realizziamo <strong>ristrutturazioni di lusso</strong> per edifici storici, ville di prestigio 
                e residenze esclusive. I nostri progetti coniugano rispetto per la tradizione architettonica 
                e comfort moderni, utilizzando materiali pregiati e maestranze specializzate.
              </p>
              <p className="text-gray-700 leading-relaxed text-justify">
                Dalle <strong>scale artistiche con illuminazione scenografica</strong> ai bagni in marmo Calacatta, 
                ogni dettaglio è curato per preservare il valore storico e aggiungere eleganza contemporanea.
              </p>
            </div>

            {/* Specializations */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Le Nostre Specializzazioni</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {specializations.map((spec, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{spec.icon}</span>
                        <h4 className="font-bold text-gray-900">{spec.name}</h4>
                      </div>
                      <p className="text-sm text-gray-600">{spec.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Features List */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Servizi Specializzati</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {features.slice(0, 5).map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                {features.slice(5).map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Vantaggi delle Ristrutturazioni di Prestigio</h3>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Star className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Realizziamo il Tuo Progetto di Prestigio</h3>
              <p className="text-purple-100 mb-6">
                Contattaci per una consulenza specializzata sui progetti di ristrutturazione 
                di lusso e edifici storici. Sopralluogo e preventivo personalizzato gratuiti.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-purple-600 hover:bg-gray-100 font-semibold"
                  onClick={() => window.open('tel:0587346342', '_self')}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Chiamaci: 0587 346342
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-purple-600"
                  onClick={() => window.open('https://api.whatsapp.com/send?phone=3477030346', '_blank')}
                >
                  <Mail className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RistrutturazioniPrestigioSection;